import React, {useEffect, useMemo, useState} from "react"
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Hidden,
  IconButton,
  MenuItem,
  Select,
} from "@material-ui/core"
import EmailIcon from "@material-ui/icons/Email"

import { Flex } from "../lib"
import getHttpsCallable from "../utils/getHttpsCallable"
import { Input } from "@material-ui/icons"
import { useNotify } from "react-admin"

const moveFile = getHttpsCallable("admin-bmw-moveFile")

interface MoveFileDialogParams {
  open: any
  handleClose: any
  handleMoveFile: any
  directory: string
  filepath: string
  claimType: string
  moving: boolean
}

const directories: {[key: string]: Array<{id: string, value:string}>} = {
  "bmw-claim": [
    {
      id: "purchaseContracts",
      value: "Purchase Contract Files (KV)",
    },
    {
      id: "purchaseContractMerged",
      value: "Merged Purchase Contract Files (KV)",
    },
    {
      id: "salesContracts",
      value: "Sales Contract Files",
    },
    {
      id: "vehicleRegistrationDocument",
      value: "Vehicle Registration Document",
    },
    {
      id: "latestMileage",
      value: "Latest Mileage (Photo)",
    },
  ],
  all: [
    {
      id: "claimPoaSignature",
      value: "Power of Attorney Signature",
    },
    {
      id: "claimFinancingAgreementSignature",
      value: "LFA Signature",
    },
    {
      id: "otherFiles",
      value: "Other Files",
    },
  ],
}
function getDirectories (claimType: string, currentDirectory: string)
    : Array<{id: string, value:string}> {
  const claimTypeDirectories = directories[claimType] ?? []

  return [
    ...claimTypeDirectories,
    ...directories.all,
  ].filter(directory => directory.id !== currentDirectory)
}

function MoveFileDialog (
  {
    open,
    handleClose,
    handleMoveFile,
    directory,
    moving,
    claimType,
  }: MoveFileDialogParams,
) {
  const directories = useMemo(() => {
    return getDirectories(claimType, directory)
  }, [claimType, directory])
  const [currentDirectory, setCurrentDirectory] = useState(directory)

  useEffect(() => {
    console.info("moving", moving)
  }, [moving])

  useEffect(() => {
    console.info("currentDirectory", currentDirectory)
  }, [currentDirectory])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
    >
      <DialogTitle>Move File</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Select the location where you wish to move this file to.
        </DialogContentText>
        <Select
          value={currentDirectory}
          disabled={moving}
          fullWidth
          onChange={(e) => {
            if (typeof e.target.value === "string") {
              setCurrentDirectory(e.target.value)
            }
          }}
        >
          {directories
            .map((dir) => (
              <MenuItem key={dir.id} value={dir.id}>
                {dir.value}
              </MenuItem>
            ))
          }
        </Select>

      </DialogContent>
      <DialogActions>
        <Button disabled={moving} onClick={handleClose} color='primary'>
          Cancel
        </Button>
        <Button
          disabled={
            moving || currentDirectory === directory || !currentDirectory
          }
          onClick={() => {
            handleMoveFile(currentDirectory)
          }}
        >
          {moving
            ? <CircularProgress size={18} />
            : "Move"
          }
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function MoveFileButton (
  {
    filePath,
    claimId,
    claimType = "claim",
  }: {
    filePath: string
    claimType: string
    claimId: string
  },
) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [moving, setMoving] = useState(false)
  const notify = useNotify()

  const directory = useMemo(() => {
    const splitPath = filePath.split("/")

    if (splitPath.length < 2) {
      return ""
    }

    return splitPath[splitPath.length - 2]
  }, [filePath])

  const filename = useMemo(() => {
    const splitPath = filePath.split("/")

    if (splitPath.length < 1) {
      return ""
    }

    return splitPath[splitPath.length - 1]
  }, [filePath])

  async function handleMoveFile (destination: string) {
    if (!destination) {
      return
    }

    if (filePath.split("/").length < 5) {
      notify("Invalid file path", "error")
    }

    console.info("Move file", filename, destination)
    const destPath = filePath.split("/").map((subpath) => {
      if (subpath === directory) {
        return destination
      }

      return subpath
    }).join("/")

    try {
      setMoving(true)
      await moveFile({
        filePath,
        destination: destPath,
      })

      notify("File " + filename + " moved to " + destination, "success")

      setMoving(false)
      setDialogOpen(false)

      location.reload()
    }
    catch (err: any) {
      setMoving(false)
      setDialogOpen(false)
      notify(err?.message, "warning")
    }
  }

  function handleCloseDialog (
    event: any,
    reason: "backdropClick" | "escapeKeyDown",
  ) {
    if (reason === "backdropClick") {
      return
    }

    setDialogOpen(false)
  }

  function handleOpenDialog () {
    setDialogOpen(true)
  }

  return (
    <>
      <MoveFileDialog
        handleClose={handleCloseDialog}
        open={dialogOpen}
        handleMoveFile={handleMoveFile}
        moving={moving}
        claimType={claimType}
        directory={directory}
        filepath={filePath}
      />

      <IconButton
        color='primary'
        onClick={handleOpenDialog}
      >
        <Input />
        {/* <Hidden mdDown>&nbsp;Send Email</Hidden> */}
      </IconButton>
    </>
  )
}
